import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";

import PackageCardAlpha from "./PackageCardAlpha";
import CtypeCheck      from "./CtypeCheck";
import ArtformCheck    from "./ArtformCheck";
import WorkshopSearch  from "./WorkshopSearch";

const API_URL   = "https://test.campfr.com/api/list_all_workshops/";
const API_TOKEN = process.env.REACT_APP_API_TOKEN;
const PAGE_SIZE = 15;                      // infinite‑scroll batch size

function WorkshopSidebarWrap() {
  /* -------------------------------------------------- */
  /* 1. raw data                                        */
  /* -------------------------------------------------- */
  const [allWorkshops, setAllWorkshops] = useState([]);
  const [error,        setError]        = useState(null);
  const [isLoading,    setIsLoading]    = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(API_URL, { headers: { "X-API-TOKEN": API_TOKEN } });
        setAllWorkshops(data);
      } catch (err) {
        setError(err.message || "Failed to fetch workshops");
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  /* -------------------------------------------------- */
  /* 2. filter state                                    */
  /* -------------------------------------------------- */
  const [searchTerm,       setSearchTerm]       = useState("");
  const [ctypeFilter,      setCtypeFilter]      = useState("all");
  const [artformFilters,   setArtformFilters]   = useState(new Set());

  /* -------------------------------------------------- */
  /* 3. apply filters                                   */
  /* -------------------------------------------------- */
  const filtered = useMemo(() => {
    return allWorkshops.filter((w) => {
      /* search */
      const text = (w.title_with + " " + w.tutors).toLowerCase();
      if (searchTerm && !text.includes(searchTerm.toLowerCase())) return false;

      /* ctype */
      if (ctypeFilter !== "all" && w.ctype !== ctypeFilter) return false;

      /* artforms */
      if (artformFilters.size) {
        const arts = w.artforms.split(",").map((s) => s.trim().toLowerCase());
        const hasOne = [...artformFilters].some((af) => arts.includes(af));
        if (!hasOne) return false;
      }

      return true;
    });
  }, [allWorkshops, searchTerm, ctypeFilter, artformFilters]);

  /* -------------------------------------------------- */
  /* 4. client‑side pagination / infinite scroll        */
  /* -------------------------------------------------- */
  const [page, setPage] = useState(1);
  const loadMore = () => setPage((p) => p + 1);

  /* reset to first page whenever filters change */
  useEffect(() => setPage(1), [filtered]);

  const visible = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = visible.length < filtered.length;

  /* Intersection‑observer sentinel */
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!hasMore) return;

    const io = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && loadMore(),
      { rootMargin: "200px" }
    );
    if (sentinelRef.current) io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [hasMore]);

  /* -------------------------------------------------- */
  /* 5. render                                          */
  /* -------------------------------------------------- */
  if (isLoading) return <p>Loading workshops…</p>;
  if (error)     return <p className="text-danger">{error}</p>;

  return (
    <div className="package-sidebar-wrapper pt-110">
      <div className="container">
        <div className="row">
          {/* cards */}
          <div className="col-lg-9">
            <div className="row g-4">
              {visible.map((w) => (
                <div className="col-lg-4 col-md-6 fadeffect" key={w.id}>
                  <PackageCardAlpha workshop={w} />
                </div>
              ))}
            </div>

            {/* infinite‑scroll sentinel & fallback button */}
            <div ref={sentinelRef} />
            {hasMore && (
              <div className="text-center mt-4">
                <button onClick={loadMore} className="btn btn-primary">Load more</button>
              </div>
            )}
            {!visible.length && <p>No workshops match your filters.</p>}
          </div>

          {/* sidebar */}
          <div className="col-lg-3">
            <div className="package-sidebar">
              <WorkshopSearch value={searchTerm} onChange={setSearchTerm} />
              <CtypeCheck    value={ctypeFilter} onChange={setCtypeFilter}   />
              <ArtformCheck  value={artformFilters} onChange={setArtformFilters} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkshopSidebarWrap;
